// Global constant for valid shop country codes.
const VALID_SHOP_COUNTRY_CODES = ["en_GB", "en_US"];

import Vue from 'vue';
import Vuex from 'vuex';
import { data } from '../shared';

Vue.use(Vuex);

const state = () => ({
  view: "loading",
  filterItems: [],
  searchComponent: undefined,
  objectives: [],
  filteredObjectives: [],
  objectivesToCompare: [],
  contrastMethodSets: {},
  translations: {},
  selectedObjective: undefined,
  selectedObjectiveComponent: undefined,
  selectedDetailTabIndex: 0,
  appliedFilters: {},
  contactURL: undefined,
  sorting: "sorting",
  listOutOfView: false,
  lastFilterOption: undefined,
  countryCode: '',      // Stores the country code
  shopEnabled: false,    // New boolean state indicating if the Shop filter is enabled
  showBanner: false,    // Changed to false by default, will be set based on shopEnabled
});

const mutations = {
  setView(state, view) {
    state.view = view;
  },
  setFilters(state, payload) {
    const { filters, countryCode } = payload;
    // Reset the filterItems array
    state.filterItems = [];

    // Iterate over the filters provided by the JSON
    for (const [key, value] of Object.entries(filters)) {
      value.id = parseInt(key);
      // Convert filterOptions from object to array for consistency.
      value.filterOptions = Object.values(value.filterOptions);

      // Check if this filter is the Shop filter.
      if (value.filterItemID === "Shop") {
        if (state.shopEnabled) {
          // Update the Shop filter's option with the current countryCode.
          value.filterOptions.forEach((option) => {
            // Set the filter value to the current country code.
            option.filterValues = { "1": countryCode };
          });
          state.filterItems.push(value);
        }
        // If shop is disabled, do not add the Shop filter.
      } else {
        // Add all non-Shop filters as-is.
        state.filterItems.push(value);
      }
    }
  },
  setObjectives(state, objectives) {
    state.objectives = objectives;
  },
  setFilteredObjectives(state, objectives) {
    state.filteredObjectives = objectives;
  },
  setTranslations(state, translations) {
    state.translations = translations;
  },
  setContrastMethodSets(state, sets) {
    state.contrastMethodSets = sets;
  },
  setSelectedObjective(state, objective) {
    state.selectedObjective = objective;
  },
  setSelectedObjectiveComponent(state, objectiveComponent) {
    state.selectedObjectiveComponent = objectiveComponent;
  },
  setSelectedDetailTabIndex(state, indx) {
    state.selectedDetailTabIndex = indx;
  },
  setContactURL(state, url) {
    state.contactURL = url;
  },
  setListOutOfView(state, isOut) {
    state.listOutOfView = isOut;
  },
  setLastFilterOption(state, filterComponent) {
    state.lastFilterOption = filterComponent;
  },
  addAppliedFilter(state, filterItemSettings) {
    state.appliedFilters[filterItemSettings.id] = filterItemSettings;
  },
  addObjectiveToCompare(state, objective) {
    state.objectivesToCompare.push(objective);
  },
  removeObjectiveToCompare(state, objective) {
    state.objectivesToCompare = state.objectivesToCompare.filter((obj) => obj !== objective);
  },
  clearObjectivesToCompare(state) {
    state.objectivesToCompare = [];
  },
  removeAppliedFilter(state, id) {
    delete state.appliedFilters[id];
  },
  sortFilteredObjectives(state, sorting) {
    state.filteredObjectives
      .sort(function (a, b) {
        return a[sorting] - b[sorting];
      })
      .reverse();
  },
  uncheckAllFilters(state) {
    state.filterItems.map((f) => {
      return f.filterOptions.map((o) => {
        return (o.checked = false);
      });
    });
    state.searchComponent.clear();
  },
  registerSearchComponent(state, searchComponent) {
    state.searchComponent = searchComponent;
  },
  updateFilteredObjectives(state) {
    state.filteredObjectives = [...state.objectives]; // Todo?

    for (let p in state.appliedFilters) {
      let f = state.appliedFilters[p];

      switch (f.type) {
        case "array":
          if (f.logicalOperator === "OR") {
            state.filteredObjectives = state.filteredObjectives.filter((obj) => {
              return f.values.filter((v) => obj[f.targetProperty].includes(v)).length;
            });
          } else if (f.logicalOperator === "AND") {
            state.filteredObjectives = state.filteredObjectives.filter((obj) => {
              return f.values.every(function (val) {
                return obj[f.targetProperty].indexOf(val) !== -1;
              });
            });
          }
          break;

        case "string":
          state.filteredObjectives = state.filteredObjectives.filter((obj) => {
            return f.values.some((element) => {
              return element === obj[f.targetProperty];
            });
          });
          break;

        case "fuzzyString":
          state.filteredObjectives = state.filteredObjectives.filter((obj) => {
            return obj[f.targetProperty].toString().includes(f.values[0]);
          });
          break;

        case "float":
          state.filteredObjectives = state.filteredObjectives.filter((obj) => {
            let match = false;
            for (let i = 0; i < f.values.length; i++) {
              if (obj[f.targetProperty] >= f.values[i].valueMin && obj[f.targetProperty] <= f.values[i].valueMax) {
                match = true;
                break;
              }
            }
            return match;
          });
          break;

        default:
          break;
      }
    }
  },
  setCountryCode(state, countryCode) {
    state.countryCode = countryCode;
  },
  setShopEnabled(state, isEnabled) {
    state.shopEnabled = isEnabled;
  },
  setShowBanner(state, isVisible) {
    state.showBanner = isVisible;
  }
};

const actions = {
  async loadAllData({ commit }) {
    const allData = await data.getAllData();

    // Retrieve the country code first.
    let countryCode = '';
    if (Vue.prototype.$dmfGetCountryCode) {
      countryCode = await Vue.prototype.$dmfGetCountryCode();
      // countryCode = "en_US"; // For testing purposes
      commit("setCountryCode", countryCode);

      // Use the global constant to determine if the shop should be enabled.
      const shopEnabled = VALID_SHOP_COUNTRY_CODES.includes(countryCode);
      commit("setShopEnabled", shopEnabled);

      // Set showBanner based on shopEnabled value
      commit("setShowBanner", shopEnabled);
    }

    // Now set up filters using the country code.
    commit("setFilters", { filters: allData.filters, countryCode });

    commit("setObjectives", allData.results);
    commit("setFilteredObjectives", allData.results);
    commit("setTranslations", allData.translations);
    commit("setContrastMethodSets", allData.contrastMethodSets);
    commit("setView", "list");
    commit("setContactURL", allData.extra.contactUs);
    commit("sortFilteredObjectives", "sorting");
  },
  setView({ commit }, view) {
    commit("setView", view);
    this.dispatch("selectObjective", {
      objectiveComponent: undefined,
      targetTabIndex: 0,
    });
  },
  setContactURL({ commit }, url) {
    commit("setContactURL", url);
  },
  selectObjective({ commit }, { objectiveComponent, targetTabIndex = 0 }) {
    commit("setSelectedObjective", objectiveComponent ? objectiveComponent.specs : undefined);
    commit("setSelectedObjectiveComponent", objectiveComponent);
    commit("setSelectedDetailTabIndex", targetTabIndex);
  },
  setSelectedDetailTabIndex({ commit }, targetTabIndex) {
    commit("setSelectedDetailTabIndex", targetTabIndex);
  },
  setListOutOfView({ commit }, isOut) {
    commit("setListOutOfView", isOut);
  },
  setLastFilterOption({ commit }, filterComponent) {
    commit("setLastFilterOption", filterComponent);
  },
  addObjectiveToCompare({ commit }, objective) {
    commit("addObjectiveToCompare", objective);
  },
  removeObjectiveToCompare({ commit }, objective) {
    commit("removeObjectiveToCompare", objective);
  },
  clearObjectivesToCompare({ commit }) {
    commit("clearObjectivesToCompare");
  },
  addAppliedFilter({ commit }, filterItemSettings) {
    commit("addAppliedFilter", filterItemSettings);
    commit("updateFilteredObjectives");
    this.dispatch("selectObjective", {
      objectiveComponent: undefined,
      targetTabIndex: 0,
    });
  },
  removeAppliedFilter({ commit }, filterItemSettings) {
    commit("removeAppliedFilter", filterItemSettings.id);
    commit("updateFilteredObjectives");
    this.dispatch("selectObjective", {
      objectiveComponent: undefined,
      targetTabIndex: 0,
    });
  },
  sortFilteredObjectives({ commit }, event) {
    commit("sortFilteredObjectives", event.target.value);
  },
  uncheckAllFilters({ commit }) {
    commit("uncheckAllFilters");
  },
  registerSearchComponent({ commit }, searchComponent) {
    commit("registerSearchComponent", searchComponent);
  },
  setCountryCode({ commit }, countryCode) {
    commit("setCountryCode", countryCode);
  },
  setShowBanner({ commit }, isVisible) {
    commit("setShowBanner", isVisible);
  }
};

export const getters = {
  countryCode: (state) => state.countryCode,
  shopEnabled: (state) => state.shopEnabled,
  showBanner: (state) => state.showBanner
};

export default new Vuex.Store({
  // strict: process.env.NODE_ENV !== 'production',
  state,
  mutations,
  actions,
  getters,
});
